/**
 * Προσφορές, εκπτώσεις και ομάδες διδύμων — ΜΟΝΟ ΑΝΑΓΝΩΣΗ (§13.7 επίπεδο E, §13.11).
 *
 * ΓΙΑΤΙ υπάρχει αν δεν επεξεργάζεται τίποτα: η προσφορά είναι ο λόγος που ένα
 * είδος υπάρχει ΔΕΥΤΕΡΗ φορά με άλλο κωδικό (§13.1). Όταν ο χειριστής δει
 * «Freddo espresso ×3» στο πλέγμα, εδώ βρίσκει ΠΟΙΑ προσφορά το γέννησε, αν είναι
 * ενεργή, και ποια ακριβώς είδη-δίδυμα κρέμονται από αυτήν. Χωρίς αυτό, το «×3»
 * μοιάζει με σφάλμα δεδομένων και ο χειριστής προσπαθεί να το «καθαρίσει».
 */

import React from 'react';
import { Download, Gift, Loader2, Percent } from 'lucide-react';
import { StoreSummary, TwinsResponse } from '../../services/platformCatalogApi';
import { fmtMoneyEuro } from './catalogUi';

interface Props {
  stores: StoreSummary[];
  storeId: number | null;
  onStoreChange: (storeId: number) => void;
  data: TwinsResponse | null;
  loading: boolean;
  worklistBusy: boolean;
  onDownloadWorklist: () => void;
}

const OffersPanel: React.FC<Props> = ({
  stores, storeId, onStoreChange, data, loading, worklistBusy, onDownloadWorklist,
}) => {
  const groups = data ? data.groups.filter(g => g.members > 1) : [];

  return (
    <div className="border border-line rounded-xl p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h4 className="text-sm font-semibold text-t1 flex items-center gap-2">
          <Gift className="w-4 h-4 text-violet-600 dark:text-violet-400" /> Προσφορές &amp; εκπτώσεις (μόνο ανάγνωση)
        </h4>
        <select
          value={storeId === null ? '' : String(storeId)}
          onChange={e => e.target.value !== '' && onStoreChange(Number(e.target.value))}
          className="bg-surface-0 border border-line-strong rounded-lg px-3 py-1.5 text-sm text-t1"
        >
          <option value="">Διάλεξε κατάστημα…</option>
          {stores.map(s => <option key={s.store_id} value={s.store_id}>{s.store_name}</option>)}
        </select>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-t3" />}
        <button
          type="button"
          onClick={onDownloadWorklist}
          disabled={storeId === null || worklistBusy}
          title="Κατεβάζει τη λίστα εργασίας αυτού του καταστήματος σε CSV (για εκτύπωση ή για τον καταστηματάρχη)"
          className="ml-auto px-3 py-1.5 bg-surface-2 hover:bg-surface-3 disabled:opacity-50 text-t1 border border-line-strong rounded-lg text-sm flex items-center gap-2"
        >
          {worklistBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Λίστα εργασίας (CSV)
        </button>
      </div>

      <p className="text-xs text-t3">
        Οι προσφορές (1+1, efood Deals) και οι εκπτώσεις <strong>δεν αλλάζουν από εδώ</strong>: το efood δεν τις
        δέχεται μέσω API. Εμφανίζονται για να ξέρεις ποια είδη-δίδυμα κρέμονται από κάθε προσφορά — αλλαγή
        τιμής μόνο στο κύριο είδος αφήνει σιωπηλά την προσφορά στην παλιά τιμή.
      </p>

      {storeId === null ? (
        <p className="text-sm text-t4">Διάλεξε κατάστημα για να δεις τις προσφορές του.</p>
      ) : data && !data.has_offers ? (
        <p className="text-xs text-t4">
          Ο server δεν στέλνει ακόμη προσφορές/εκπτώσεις σε αυτή τη διαδρομή — παρακάτω φαίνονται οι ομάδες
          διδύμων, που είναι το αποτύπωμά τους στον κατάλογο.
        </p>
      ) : null}

      {data && data.offers.length > 0 && (
        <div className="space-y-2">
          {data.offers.map(offer => (
            <div key={offer.platform_code} className="border border-line rounded-lg p-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-t1">{offer.title || `Προσφορά ${offer.platform_code}`}</span>
                <span className={`px-1.5 py-0.5 rounded-lg text-[11px] ${
                  offer.is_active
                    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                    : 'bg-surface-3 text-t3'}`}
                >
                  {offer.is_active ? 'ενεργή' : 'ανενεργή'}
                </span>
                <span className="text-[11px] text-t4 font-mono">{offer.platform_code}</span>
              </div>
              {offer.offer_line && <p className="text-xs text-t3 mt-0.5">{offer.offer_line}</p>}
              {offer.items && offer.items.length > 0 && (
                <ul className="mt-1.5 space-y-0.5">
                  {offer.items.map(it => (
                    <li key={it.item_code} className="text-xs text-t2 flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${it.is_available ? 'bg-emerald-500' : 'bg-red-500'}`} />
                      <span className="truncate">{it.name || it.item_code}</span>
                      <span className="text-t4 font-mono text-[10px]">{it.item_code}</span>
                      <span className="ml-auto tabular-nums">{fmtMoneyEuro(it.price)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {(!offer.items || offer.items.length === 0) && offer.category_codes && offer.category_codes.length > 0 && (
                <p className="text-[11px] text-t4 mt-1 font-mono">
                  κατηγορίες: {offer.category_codes.join(' · ')}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {data && data.discounts.length > 0 && (
        <div className="border border-line rounded-lg p-2.5">
          <div className="text-xs font-medium text-t2 flex items-center gap-1.5">
            <Percent className="w-3.5 h-3.5" /> Εκπτώσεις
          </div>
          <ul className="mt-1 space-y-0.5">
            {data.discounts.map((d, i) => (
              <li key={`${d.code || i}`} className="text-xs text-t3">
                {d.title || d.code || `Έκπτωση ${i + 1}`}
                {d.value !== undefined && d.value !== null ? ` — ${d.value}` : ''}
                {d.is_active === false ? ' (ανενεργή)' : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {groups.length > 0 && (
        <div className="border border-line rounded-lg p-2.5">
          <div className="text-xs font-medium text-t2">Ομάδες διδύμων ({groups.length})</div>
          <ul className="mt-1 space-y-1">
            {groups.map(g => (
              <li key={g.twin_key} className="text-xs text-t2">
                <span className="text-t1">«{g.twin_key}»</span>
                <span className="text-t3"> · {g.members} μέλη</span>
                {!g.prices_agree && (
                  <span className="ml-1 px-1 rounded text-[10px] bg-amber-500/20 text-amber-800 dark:text-amber-200"
                    title="Τα μέλη έχουν ΗΔΗ διαφορετικές τιμές — δεν αλλάζουν μαζί αυτόματα">
                    διαφορετικές τιμές
                  </span>
                )}
                <span className="block text-[10px] text-t4 font-mono">
                  {g.codes.map(code => `${code}${g.roles && g.roles[code] === 'offer_shadow' ? ' (προσφορά)' : ''}`).join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {storeId !== null && !loading && data && data.offers.length === 0 && groups.length === 0 && (
        <p className="text-sm text-t4">Αυτό το κατάστημα δεν έχει προσφορές ούτε δίδυμα τιμής.</p>
      )}
    </div>
  );
};

export default OffersPanel;
